/**
 * Chart API utilities.
 * Pure functions for creating the chart API object.
 * No React dependencies.
 */

import type {
  ChartStore,
  ChartTimeRange,
  MarketBarMessageType,
  OhlcvPoint,
  PricePoint,
  TickerChartApi,
  TickerSeries,
} from "~types/types";

/**
 * Create a chart API object from a store reference.
 * The storeRef is a mutable ref object that always points to the current store.
 * This allows the API methods to work correctly even if the store instance changes.
 */
export function createChartApi(getStore: () => ChartStore): TickerChartApi {
  return {
    appendData: (
      seriesId: string,
      points: Array<PricePoint | OhlcvPoint>,
      messageType?: MarketBarMessageType
    ): void => {
      getStore().appendData(seriesId, points, messageType);
    },

    setSeriesData: (
      seriesId: string,
      data: Array<PricePoint | OhlcvPoint>
    ): void => {
      getStore().setSeriesData(seriesId, data);
    },

    addSeries: (series: TickerSeries): void => {
      getStore().addSeries(series);
    },

    removeSeries: (seriesId: string): void => {
      getStore().removeSeries(seriesId);
    },

    setVisibleRange: (range: ChartTimeRange): void => {
      getStore().setViewport({ start: range.start, end: range.end });
    },

    resetView: (): void => {
      getStore().resetView();
    },

    zoomIn: (factor = 1.5): void => {
      const store = getStore();
      const layout = store.getLayout();
      const centerX = layout.priceArea.x + layout.priceArea.width / 2;

      store.zoom(factor, centerX);
    },

    zoomOut: (factor = 1.5): void => {
      const store = getStore();
      const layout = store.getLayout();
      const centerX = layout.priceArea.x + layout.priceArea.width / 2;

      store.zoom(1 / factor, centerX);
    },

    panBy: (deltaMs: number): void => {
      const store = getStore();
      const viewport = store.getViewport();
      const layout = store.getLayout();
      const msPerPixel =
        (viewport.end - viewport.start) / layout.priceArea.width;
      const deltaPixels = deltaMs / msPerPixel;

      store.pan(deltaPixels);
    },

    getSeries: (): TickerSeries[] => {
      const state = getStore().getState();

      return Array.from(state.seriesMeta.values());
    },

    getVisibleRange: (): ChartTimeRange => {
      const viewport = getStore().getViewport();

      return { start: viewport.start, end: viewport.end };
    },
  };
}
